import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, ChevronLeft, ChevronRight, Upload, FileText, Trash2,
  Loader2, Plus, Check, Building2, Home, Building, Store,
  Calendar, AlertCircle, Send,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { TRADES, tradeLabel, tradeBidTemplate, isTradeTemplate } from '@/lib/trades';
import { formatDate } from '@/lib/dateUtils';
import { BuilderCompanyPicker } from '@/components/BuilderCompanyPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NewBidProjectModal — v2.5 Project Hub redesign, Phase 1.
 *
 * Two-step flow for creating a project that's gathering bids before going
 * to a bank:
 *
 *   Step 1 · Project Info
 *     - Name, address, project type (chips), conditional unit count,
 *       start date, project details (free text), plans upload (cascades
 *       to every scope automatically).
 *     - Saves to db.projects with bid_workflow_status="draft".
 *
 *   Step 2 · Scopes of Work
 *     - Inline list of scope drafts, "+ Add another scope" button opens
 *       a small inner form. Each scope = a bid_request bound to the
 *       project_id and inheriting the plans.
 *     - Auto-nudge default ON. Avatar preview of contractors who will be
 *       notified when the user hits Publish.
 *
 *   Final action: Publish All → flips project to "gathering_bids" and
 *   creates the bid_requests in MongoDB. Auto-nudge emails fire from the
 *   existing bid_request endpoint, so GC awareness etc. is preserved.
 */
export default function NewBidProjectModal({
  isOpen,
  onClose,
  userId,
  contractors = [],
  onPublished,
}) {
  const [step, setStep] = useState(1);
  const [projectId, setProjectId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  // Step 1 fields
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [propertyType, setPropertyType] = useState(''); // sfh / multifamily / mixed_use / commercial
  const [units, setUnits] = useState('');
  // Multi-unit / multi-building (Phase 2)
  const [buildings, setBuildings] = useState('');
  const [unitsPerBuilding, setUnitsPerBuilding] = useState([]); // [num, num, ...]
  const [startDate, setStartDate] = useState('');
  const [details, setDetails] = useState('');
  const [autoNudge, setAutoNudge] = useState(true);
  // Builder company (v2.6.2)
  const [builder, setBuilder] = useState({ id: null, name: null, logo: null });

  // Plans upload state
  const [plans, setPlans] = useState([]); // {id, filename, file_id, size_bytes}
  const [isUploadingPlans, setIsUploadingPlans] = useState(false);

  // Step 2 — scope drafts
  const [scopes, setScopes] = useState([]);
  // Inline "add scope" form
  const [showAddScope, setShowAddScope] = useState(false);
  const [newScopeTitle, setNewScopeTitle] = useState('');
  const [newScopeTrade, setNewScopeTrade] = useState('');
  const [newScopeDue, setNewScopeDue] = useState('');
  const [newScopeNotes, setNewScopeNotes] = useState('');
  // Per-scope fixture/spec attachments (in addition to project-wide plans)
  const [newScopeFiles, setNewScopeFiles] = useState([]); // {id, filename, file_id, size_bytes}
  const [isUploadingScopeFiles, setIsUploadingScopeFiles] = useState(false);

  // ---- Derived ----
  const showUnitField = ['multifamily', 'mixed_use', 'commercial'].includes(propertyType);

  // Sync per-building array when `buildings` count changes — seeds with even-split
  // (any remainder goes to the last building) so the form starts in a valid state
  // for common cases like 8 units / 4 bldgs → [2,2,2,2].
  useEffect(() => {
    const n = parseInt(buildings, 10);
    const totalUnits = parseInt(units, 10);
    if (!Number.isFinite(n) || n < 1) return;
    setUnitsPerBuilding(prev => {
      // If user has already edited and the array length matches, keep their values
      if (prev.length === n && prev.some(v => v !== 1)) return prev;
      if (Number.isFinite(totalUnits) && totalUnits >= n) {
        const base = Math.floor(totalUnits / n);
        const rem = totalUnits - base * n;
        return Array.from({ length: n }, (_, i) => base + (i === n - 1 ? rem : 0));
      }
      // Fallback: pad with 1s when total isn't known yet
      const next = [...prev];
      while (next.length < n) next.push(1);
      next.length = n;
      return next;
    });
  }, [buildings, units]);

  const upbSum = unitsPerBuilding.reduce((s, x) => s + (parseInt(x, 10) || 0), 0);

  // Auto-compute default bid due date = start - 14d
  const defaultBidDue = useMemo(() => {
    if (!startDate) return '';
    try {
      const d = new Date(startDate);
      d.setDate(d.getDate() - 14);
      return d.toISOString().split('T')[0];
    } catch { return ''; }
  }, [startDate]);

  // Reset when reopened
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setProjectId(null);
    setName('');
    setAddress('');
    setPropertyType('');
    setUnits('');
    setBuildings('');
    setUnitsPerBuilding([]);
    setStartDate('');
    setDetails('');
    setAutoNudge(true);
    setBuilder({ id: null, name: null, logo: null });
    setPlans([]);
    setScopes([]);
    setShowAddScope(false);
    setNewScopeTitle('');
    setNewScopeTrade('');
    setNewScopeDue('');
    setNewScopeNotes('');
    setNewScopeFiles([]);
  }, [isOpen]);

  // ---- Step 1: Save project draft ----
  const validateStep1 = () => {
    if (!name.trim()) return 'Project name is required';
    if (!address.trim()) return 'Property address is required';
    if (!propertyType) return 'Project type is required';
    if (showUnitField && (!units || parseInt(units, 10) < 1)) {
      return 'Number of units is required for Multifamily / Mixed-Use / Commercial';
    }
    if (showUnitField && parseInt(units, 10) > 1) {
      // Multi-unit projects must also specify buildings + per-building unit grid
      const b = parseInt(buildings, 10);
      if (!b || b < 1) return 'Number of buildings is required';
      if (upbSum !== parseInt(units, 10)) {
        return `Units per building must sum to ${units} (currently ${upbSum})`;
      }
    }
    if (!startDate) return 'Project start date is required';
    if (plans.length === 0) return 'At least one plans / SOW file is required';
    return null;
  };

  const saveDraftAndContinue = async () => {
    const err = validateStep1();
    if (err) { toast.error(err); return; }

    setIsSaving(true);
    try {
      if (!projectId) {
        // Create new draft
        const r = await axios.post(`${API}/projects/bid-draft`, {
          user_id: userId,
          name: name.trim(),
          address: address.trim(),
          property_type: propertyType,
          units: showUnitField ? parseInt(units, 10) : 1,
          buildings: (showUnitField && parseInt(units, 10) > 1) ? parseInt(buildings, 10) : null,
          units_per_building: (showUnitField && parseInt(units, 10) > 1)
            ? unitsPerBuilding.map(n => parseInt(n, 10) || 1)
            : null,
          start_date: new Date(startDate).toISOString(),
          project_details: details.trim() || null,
          auto_nudge_enabled: autoNudge,
          builder_company_id: builder.id,
          builder_company_name: builder.name,
          builder_company_logo: builder.logo,
        });
        setProjectId(r.data.id);
      } else {
        // Patch existing draft
        await axios.patch(`${API}/projects/${projectId}/bid-draft`, {
          name: name.trim(),
          address: address.trim(),
          property_type: propertyType,
          units: showUnitField ? parseInt(units, 10) : 1,
          buildings: (showUnitField && parseInt(units, 10) > 1) ? parseInt(buildings, 10) : null,
          units_per_building: (showUnitField && parseInt(units, 10) > 1)
            ? unitsPerBuilding.map(n => parseInt(n, 10) || 1)
            : null,
          start_date: new Date(startDate).toISOString(),
          project_details: details.trim() || null,
          auto_nudge_enabled: autoNudge,
          builder_company_id: builder.id,
          builder_company_name: builder.name,
          builder_company_logo: builder.logo,
        });
      }
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save draft');
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Plans upload (requires projectId — auto-create draft if needed) ----
  const handlePlanFiles = async (files) => {
    if (!files || files.length === 0) return;

    // Ensure we have a project_id before uploading. If user hasn't completed
    // Step 1 fields yet, create a minimal draft with placeholder name.
    let pid = projectId;
    if (!pid) {
      if (!name.trim() || !address.trim() || !propertyType) {
        toast.error('Fill name, address, and project type before uploading plans');
        return;
      }
      try {
        const r = await axios.post(`${API}/projects/bid-draft`, {
          user_id: userId,
          name: name.trim(),
          address: address.trim(),
          property_type: propertyType,
          units: showUnitField ? parseInt(units || '1', 10) : 1,
          start_date: startDate ? new Date(startDate).toISOString() : null,
          project_details: details.trim() || null,
          auto_nudge_enabled: autoNudge,
          builder_company_id: builder.id,
          builder_company_name: builder.name,
          builder_company_logo: builder.logo,
        });
        pid = r.data.id;
        setProjectId(pid);
      } catch (e) {
        toast.error('Failed to start project draft');
        return;
      }
    }

    setIsUploadingPlans(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('files', f));
      const r = await axios.post(`${API}/projects/${pid}/bid-plans`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPlans(r.data.bid_plans || []);
      toast.success(`Uploaded ${r.data.added} file(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Plan upload failed');
    } finally {
      setIsUploadingPlans(false);
    }
  };

  const handleRemovePlan = async (planId) => {
    if (!projectId) return;
    try {
      const r = await axios.delete(`${API}/projects/${projectId}/bid-plans/${planId}`);
      setPlans(r.data.bid_plans || []);
    } catch (e) {
      toast.error('Could not remove plan');
    }
  };

  // ---- Step 2: scope management (local until Publish) ----
  // When the trade changes, auto-fill the notes with a trade-specific template
  // asking the contractor to bid the attached plans — unless the user has
  // already typed their own notes.
  const handleTradeChange = (trade) => {
    setNewScopeTrade(trade);
    if (isTradeTemplate(newScopeNotes)) {
      setNewScopeNotes(trade ? tradeBidTemplate(trade) : '');
    }
  };

  // ---- Per-scope fixture upload ----
  const handleScopeFiles = async (files) => {
    if (!files || files.length === 0) return;
    let pid = projectId;
    if (!pid) { toast.error('Save the project first (Step 1) before attaching fixtures'); return; }
    setIsUploadingScopeFiles(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('files', f));
      const r = await axios.post(`${API}/projects/${pid}/bid-scope-files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setNewScopeFiles(prev => [...prev, ...(r.data.scope_attachments || [])]);
      toast.success(`Attached ${r.data.added} fixture file(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fixture upload failed');
    } finally {
      setIsUploadingScopeFiles(false);
    }
  };

  const removeScopeFile = (fileId) => {
    setNewScopeFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const addScope = () => {
    if (!newScopeTitle.trim()) { toast.error('Scope title required'); return; }
    if (!newScopeTrade) { toast.error('Trade required'); return; }
    setScopes(prev => [...prev, {
      tmp_id: `tmp-${Date.now()}`,
      title: newScopeTitle.trim(),
      trade: newScopeTrade,
      due_date: newScopeDue || defaultBidDue,
      notes: newScopeNotes.trim(),
      scope_attachments: newScopeFiles,
    }]);
    setNewScopeTitle('');
    setNewScopeTrade('');
    setNewScopeDue('');
    setNewScopeNotes('');
    setNewScopeFiles([]);
    setShowAddScope(false);
  };

  const removeScope = (tmpId) => {
    setScopes(prev => prev.filter(s => s.tmp_id !== tmpId));
  };

  // Compute who would be auto-notified based on contractor trades
  const matchedContractors = useMemo(() => {
    if (!autoNudge) return [];
    const tradesInScopes = new Set(scopes.map(s => (s.trade || '').toLowerCase()));
    const result = [];
    contractors.forEach(c => {
      const cTrades = [
        c.trade, c.specialty,
        ...((c.trades || [])),
      ].filter(Boolean).map(t => String(t).toLowerCase());
      const isGC = cTrades.some(t => ['general', 'general contractor', 'gc', 'general contracting'].includes(t));
      const matchesSpecific = cTrades.some(t => tradesInScopes.has(t));
      if (isGC || matchesSpecific) {
        result.push({ ...c, isGC });
      }
    });
    return result;
  }, [autoNudge, scopes, contractors]);

  // ---- Publish: create bid_requests + flip project status ----
  const publishAll = async () => {
    if (scopes.length === 0) { toast.error('Add at least one scope before publishing'); return; }
    if (!projectId) { toast.error('Project draft missing'); return; }

    setIsPublishing(true);
    try {
      // Create each bid_request. The existing /api/bid-requests endpoint
      // already handles contractor notifications + GC auto-show wiring.
      const createdRequests = [];
      for (const s of scopes) {
        const dueDate = s.due_date || defaultBidDue || new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0];
        // If auto-nudge is OFF, send to empty contractor_ids list (no emails).
        // The GC auto-show portal filter still applies via trade_type matching.
        const r = await axios.post(`${API}/bid-requests`, null, {
          params: { user_id: userId },
          data: {
            project_id: projectId,
            title: s.title,
            scope_of_work: s.notes || s.title,
            trade_type: s.trade,
            due_date: dueDate,
            contractor_ids: [],
            document_ids: [],
            scope_attachments: s.scope_attachments || [],
            line_items: [],
            notes: s.notes || '',
            skip_email: !autoNudge, // Suppress email if auto-nudge off
          },
        }).catch(async (err) => {
          // Some legacy endpoints expect body in `json` not `data` — retry
          if (err?.response?.status === 422) {
            return axios.post(
              `${API}/bid-requests?user_id=${userId}`,
              {
                project_id: projectId,
                title: s.title,
                scope_of_work: s.notes || s.title,
                trade_type: s.trade,
                due_date: dueDate,
                contractor_ids: [],
                document_ids: [],
                scope_attachments: s.scope_attachments || [],
                line_items: [],
                notes: s.notes || '',
                skip_email: !autoNudge,
              },
            );
          }
          throw err;
        });
        createdRequests.push(r.data);
      }

      // Flip project to gathering_bids
      await axios.post(`${API}/projects/${projectId}/publish-bid`);

      toast.success(`Published ${createdRequests.length} scope(s) — auto-nudge ${autoNudge ? 'sent' : 'skipped'}`);
      if (onPublished) onPublished({ projectId, scopes: createdRequests });
      onClose?.();
    } catch (e) {
      console.error('Publish failed:', e);
      toast.error(e?.response?.data?.detail || 'Publish failed');
    } finally {
      setIsPublishing(false);
    }
  };

  const TYPE_CARDS = [
    { id: 'single_family', label: 'Single Family', Icon: Home },
    { id: 'multifamily', label: 'Multifamily', Icon: Building2 },
    { id: 'mixed_use', label: 'Mixed-Use', Icon: Store },
    { id: 'commercial', label: 'Commercial', Icon: Building },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto p-0"
        data-testid="new-bid-project-modal"
      >
        <VisuallyHidden>
          <DialogTitle>{step === 1 ? 'Create Project for Bid' : 'Add Scopes of Work'}</DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Create a project draft so you can publish scopes for bidding.'
              : 'Add scopes of work; each one becomes a bid request.'}
          </DialogDescription>
        </VisuallyHidden>
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {step === 1 ? 'Create Project for Bid' : 'Add Scopes of Work'}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {step === 1
                  ? 'One-time per property — plans cascade to every scope.'
                  : 'Each scope inherits your project plans automatically.'}
              </p>
            </div>
          </div>
          {/* Step pills */}
          <div className="flex gap-2 text-[10px] font-bold tracking-wider uppercase">
            <span className={`px-2.5 py-1 rounded-full ${step === 1 ? 'bg-slate-900 text-white' : 'bg-emerald-500 text-white'}`}>
              {step > 1 ? '✓ Project Info' : 'Project Info'}
            </span>
            <span className={`px-2.5 py-1 rounded-full ${step === 2 ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-500'}`}>
              Scopes
            </span>
            <span className="px-2.5 py-1 rounded-full bg-slate-200 text-slate-500">
              Publish
            </span>
          </div>
        </div>

        {/* Step 1 — Project Info */}
        {step === 1 && (
          <div className="px-6 py-5 space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Project name <span className="text-red-500">*</span>
              </Label>
              <Input
                data-testid="new-bid-project-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Ellis Row Townhomes"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Property address <span className="text-red-500">*</span>
              </Label>
              <Input
                data-testid="new-bid-project-address"
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="1319-1333 Ellis Row, Atlanta GA"
                className="mt-1"
              />
            </div>

            <BuilderCompanyPicker
              userId={userId}
              value={builder.id}
              onChange={setBuilder}
            />

            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Project type <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {TYPE_CARDS.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    data-testid={`new-bid-type-${id}`}
                    onClick={() => setPropertyType(id)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border text-sm font-semibold transition ${
                      propertyType === id
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {showUnitField && (
              <div data-testid="new-bid-unit-count-wrap">
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Number of units <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  min="1"
                  data-testid="new-bid-unit-count"
                  value={units}
                  onChange={e => setUnits(e.target.value)}
                  placeholder="e.g. 8"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Drives per-unit bid math for contractors.
                </p>
              </div>
            )}

            {/* Multi-building layout — only when units > 1 */}
            {showUnitField && parseInt(units, 10) > 1 && (
              <div data-testid="new-bid-buildings-wrap" className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/40 p-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wide text-amber-900">
                    Number of buildings <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max="32"
                    data-testid="new-bid-building-count"
                    value={buildings}
                    onChange={e => setBuildings(e.target.value)}
                    placeholder="e.g. 4"
                    className="mt-1 bg-white"
                  />
                  <p className="text-xs text-amber-800 mt-1">
                    A &quot;building&quot; is one structure on the site. Used by per-building phases like framing.
                  </p>
                </div>

                {parseInt(buildings, 10) >= 1 && unitsPerBuilding.length > 0 && (
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wide text-amber-900">
                      Units per building
                    </Label>
                    <div
                      className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
                      data-testid="new-bid-units-per-building-grid"
                    >
                      {unitsPerBuilding.map((val, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 bg-white border border-amber-300 rounded-md px-2 py-1.5">
                          <span className="text-[11px] font-semibold text-amber-900 uppercase whitespace-nowrap">
                            Bldg {idx + 1}
                          </span>
                          <input
                            type="number"
                            min="1"
                            value={val}
                            onChange={e => {
                              const next = [...unitsPerBuilding];
                              next[idx] = parseInt(e.target.value, 10) || 0;
                              setUnitsPerBuilding(next);
                            }}
                            className="w-14 px-1.5 py-0.5 text-sm font-semibold text-right border border-amber-300 rounded"
                            data-testid={`new-bid-upb-${idx}`}
                          />
                          <span className="text-[11px] text-amber-700">units</span>
                        </div>
                      ))}
                    </div>
                    <div className={`text-xs mt-2 font-medium ${
                      upbSum === parseInt(units, 10) ? 'text-emerald-700' : 'text-rose-700'
                    }`} data-testid="new-bid-upb-sum-status">
                      {upbSum === parseInt(units, 10)
                        ? `✓ Sums to ${units} units`
                        : `Sum is ${upbSum} — must equal ${units} total units`}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Project start date <span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                data-testid="new-bid-start-date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="mt-1"
              />
              {startDate && (
                <div className="mt-1 text-xs font-semibold text-slate-700 tabular-nums" data-testid="new-bid-start-display">
                  {formatDate(startDate)}
                </div>
              )}
              <p className="text-xs text-slate-500 mt-1">
                ↳ Each scope's bid due date defaults to <strong>start − 14 days</strong>{defaultBidDue ? ` (${formatDate(defaultBidDue)})` : ''}.
              </p>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Project details
              </Label>
              <Textarea
                data-testid="new-bid-project-details"
                value={details}
                onChange={e => setDetails(e.target.value)}
                placeholder="Any nuance contractors should know — special conditions, access, milestones, financing notes…"
                className="mt-1 min-h-[80px]"
              />
              <p className="text-xs text-slate-500 mt-1">
                Visible to invited contractors. Free-form — write whatever helps bid quality.
              </p>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Plans / SOW / specs <span className="text-red-500">*</span>
              </Label>
              <div className="mt-1.5 border-2 border-dashed border-slate-300 rounded-lg p-4 text-center bg-white">
                <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1.5" />
                <p className="text-sm font-semibold text-slate-700">Drop blueprints, SOW, or specs</p>
                <p className="text-xs text-slate-500">PDF · DWG · PNG · JPG — up to 100 MB</p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.dwg,.png,.jpg,.jpeg,.webp"
                  data-testid="new-bid-plans-input"
                  onChange={(e) => handlePlanFiles(e.target.files)}
                  className="block mx-auto mt-3 text-xs"
                  disabled={isUploadingPlans}
                />
                {isUploadingPlans && (
                  <div className="flex items-center justify-center gap-2 text-xs text-indigo-600 mt-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Uploading…
                  </div>
                )}
              </div>

              {plans.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {plans.map(p => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-2"
                      data-testid={`bid-plan-row-${p.id}`}
                    >
                      <div className="flex items-center gap-2 text-xs">
                        <FileText className="w-3.5 h-3.5 text-slate-500" />
                        <span className="font-semibold text-slate-800 truncate max-w-[280px]">
                          {p.filename}
                        </span>
                        <span className="text-slate-400">
                          · {(p.size_bytes / 1024 / 1024).toFixed(1)} MB
                        </span>
                      </div>
                      <button
                        onClick={() => handleRemovePlan(p.id)}
                        className="text-slate-400 hover:text-red-500"
                        data-testid={`bid-plan-remove-${p.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs text-slate-500 mt-2">
                ↳ Cascades to every scope automatically. Never re-upload per trade.
              </p>
            </div>
          </div>
        )}

        {/* Step 2 — Scopes */}
        {step === 2 && (
          <div className="px-6 py-5 space-y-4">
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2">
                Scopes ({scopes.length} {scopes.length === 1 ? 'draft' : 'drafts'})
              </div>
              {scopes.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg bg-slate-50">
                  <p className="text-sm text-slate-500">No scopes yet — add your first one below.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {scopes.map(s => (
                    <div
                      key={s.tmp_id}
                      className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-center justify-between"
                      data-testid={`scope-draft-${s.tmp_id}`}
                    >
                      <div>
                        <div className="text-sm font-bold text-slate-900">{s.title}</div>
                        <div className="text-xs text-slate-600">
                          Trade: <span>{tradeLabel(s.trade)}</span>
                          {' · '}Bid due: <span className="tabular-nums">{formatDate(s.due_date || defaultBidDue) || 'TBD'}</span>
                          {s.scope_attachments?.length > 0 && (
                            <span className="ml-1 text-indigo-600 font-semibold">· {s.scope_attachments.length} fixture file{s.scope_attachments.length === 1 ? '' : 's'}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-bold uppercase tracking-wider bg-yellow-200 text-yellow-900 px-2 py-0.5 rounded">
                          Draft
                        </span>
                        <button
                          onClick={() => removeScope(s.tmp_id)}
                          className="text-slate-400 hover:text-red-500"
                          data-testid={`scope-remove-${s.tmp_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* + Add Scope */}
            {showAddScope ? (
              <div className="border border-slate-200 rounded-lg p-3 bg-white space-y-2.5">
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-slate-600">Scope title *</Label>
                    <Input
                      data-testid="new-scope-title"
                      value={newScopeTitle}
                      onChange={e => setNewScopeTitle(e.target.value)}
                      placeholder="e.g. Insulation"
                      className="mt-1 h-9 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-slate-600">Trade *</Label>
                    <select
                      data-testid="new-scope-trade"
                      value={newScopeTrade}
                      onChange={e => handleTradeChange(e.target.value)}
                      className="mt-1 h-9 w-full text-sm rounded-md border border-slate-200 bg-white px-2"
                    >
                      <option value="" disabled>Select trade…</option>
                      {TRADES.map(t => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase text-slate-600">
                    Bid due date {defaultBidDue && <span className="text-slate-400 font-normal normal-case">(defaults to {formatDate(defaultBidDue)})</span>}
                  </Label>
                  <Input
                    type="date"
                    data-testid="new-scope-due"
                    value={newScopeDue}
                    onChange={e => setNewScopeDue(e.target.value)}
                    className="mt-1 h-9 text-sm"
                  />
                  {newScopeDue && (
                    <div className="mt-1 text-[11px] text-slate-500 tabular-nums" data-testid="new-scope-due-display">
                      {formatDate(newScopeDue)}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase text-slate-600">Bid instructions</Label>
                  <Textarea
                    data-testid="new-scope-notes"
                    value={newScopeNotes}
                    onChange={e => setNewScopeNotes(e.target.value)}
                    placeholder="Pick a trade above to auto-fill standard 'bid the plans' instructions — edit as needed."
                    className="mt-1 min-h-[90px] text-sm"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Auto-filled per trade to ask contractors to bid the attached plans. Fully editable.
                  </p>
                </div>

                {/* Per-scope fixture attachments */}
                <div>
                  <Label className="text-[10px] font-bold uppercase text-slate-600">Fixture / spec attachments (optional)</Label>
                  <div className="mt-1 border-2 border-dashed border-slate-300 rounded-lg p-3 text-center bg-white">
                    <Upload className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                    <p className="text-[11px] text-slate-500">Attach fixture cut-sheets / specs for this trade only</p>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.dwg,.png,.jpg,.jpeg,.webp"
                      data-testid="new-scope-files-input"
                      onChange={(e) => handleScopeFiles(e.target.files)}
                      className="block mx-auto mt-2 text-[11px]"
                      disabled={isUploadingScopeFiles}
                    />
                    {isUploadingScopeFiles && (
                      <div className="flex items-center justify-center gap-2 text-[11px] text-indigo-600 mt-1.5">
                        <Loader2 className="w-3 h-3 animate-spin" /> Uploading…
                      </div>
                    )}
                  </div>
                  {newScopeFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {newScopeFiles.map(f => (
                        <div key={f.id} className="flex items-center justify-between bg-slate-50 rounded-md px-2.5 py-1.5" data-testid={`scope-file-row-${f.id}`}>
                          <div className="flex items-center gap-2 text-[11px]">
                            <FileText className="w-3 h-3 text-slate-500" />
                            <span className="font-semibold text-slate-800 truncate max-w-[220px]">{f.filename}</span>
                            <span className="text-slate-400">· {(f.size_bytes / 1024 / 1024).toFixed(1)} MB</span>
                          </div>
                          <button onClick={() => removeScopeFile(f.id)} className="text-slate-400 hover:text-red-500" data-testid={`scope-file-remove-${f.id}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowAddScope(false); setNewScopeTitle(''); setNewScopeTrade(''); setNewScopeDue(''); setNewScopeNotes(''); setNewScopeFiles([]); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={addScope}
                    data-testid="confirm-add-scope-btn"
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> Add scope
                  </Button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddScope(true)}
                data-testid="add-scope-btn"
                className="w-full border-2 border-dashed border-indigo-300 rounded-lg py-3 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> Add another scope
              </button>
            )}

            {/* Auto-nudge toggle */}
            <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Auto-nudge matching contractors
                </div>
                <div className="text-xs text-slate-500">
                  GCs see all scopes. Specialists see their trade. Default ON.
                </div>
              </div>
              <Switch
                checked={autoNudge}
                onCheckedChange={setAutoNudge}
                data-testid="auto-nudge-toggle"
              />
            </div>

            {/* Will-notify avatar preview */}
            {autoNudge && matchedContractors.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 mb-2">
                  Will auto-notify on publish
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {matchedContractors.slice(0, 8).map((c, i) => (
                    <div
                      key={c.id}
                      title={`${c.contact_name || c.name || c.email}${c.isGC ? ' (GC)' : ''}`}
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                        c.isGC ? 'bg-blue-500' : 'bg-indigo-500'
                      }`}
                      style={{ marginLeft: i === 0 ? 0 : -8, border: '2px solid white' }}
                    >
                      {(c.contact_name || c.name || c.email || 'C').slice(0, 2).toUpperCase()}
                    </div>
                  ))}
                  <span className="text-xs text-slate-500 ml-2">
                    {matchedContractors.length} contractor{matchedContractors.length === 1 ? '' : 's'}{' '}
                    ({matchedContractors.filter(c => c.isGC).length} GC{matchedContractors.filter(c => c.isGC).length === 1 ? '' : 's'})
                  </span>
                </div>
              </div>
            )}

            {autoNudge && matchedContractors.length === 0 && scopes.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-800">
                  No contractors in your network match these trades. Either add contractors first, or invite them manually after publishing.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-slate-50 flex items-center justify-between sticky bottom-0">
          {step === 2 ? (
            <Button variant="ghost" size="sm" onClick={() => setStep(1)} data-testid="back-to-step1">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          )}
          {step === 1 ? (
            <Button
              onClick={saveDraftAndContinue}
              disabled={isSaving}
              data-testid="continue-to-scopes"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Continue → Add Scopes <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={publishAll}
              disabled={isPublishing || scopes.length === 0}
              data-testid="publish-all-scopes"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {isPublishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Publish {scopes.length} {scopes.length === 1 ? 'Scope' : 'Scopes'} →
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
